"use client";

import React from "react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Button } from "@/app/_module/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { cn } from "@/app/_module/lib/utils";
import {
  A2PVerificationData,
  RECOMMENDED_USE_CASE,
  SAMPLE_MESSAGE_TEMPLATES,
  personalizeTemplate,
} from "@/app/_module/types/a2p.types";
import type { RegistrationType } from "./Step0RegistrationTypeSelect";
import { Panel, Section, focusRing } from "./overview/primitives";
import { Field, ReadOnlyField, StepIntro, controlClass } from "./overview/wizard-ui";

// Words that trigger TCR rejection — block these in campaign description
const REJECTION_TRIGGER_WORDS = [
  'purchased leads', 'lead vendor', 'bought list', 'cold text', 'mass text',
  'lead list', 'data provider', 'third party leads', 'bought leads',
  'purchased list', 'cold call list', 'web scraping',
];

export default function Step3CampaignInfo({ registrationType }: { registrationType?: RegistrationType | null }) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<A2PVerificationData>();

  // Get agent name and business name for personalizing templates
  const businessName = watch('businessName') || '';
  const authorizedRepFirstName = watch('authorizedRepFirstName') || '';
  const authorizedRepLastName = watch('authorizedRepLastName') || '';
  const agentName = [authorizedRepFirstName, authorizedRepLastName].filter(Boolean).join(' ') || businessName;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "sampleMessages",
  });

  const sampleMessages = watch("sampleMessages") || [];
  const campaignDescription = watch("campaignDescription") || "";
  const soleProp = registrationType === 'sole_proprietor';
  const invalid = (bad: boolean) => cn(controlClass, bad && "!border-red-700");

  return (
    <div className="space-y-8">
      <StepIntro title="Your messages">
        <p>
          Tell the carriers how you&apos;ll text your clients. We filled these in for you. Edits are fine, but big
          changes can get a registration rejected.
        </p>
      </StepIntro>

      <Section id="a2p-campaign" title="Campaign">
        <Panel className="space-y-5 px-4 py-5 sm:px-5">
          <Field id="campaignName" label="Campaign name" error={errors.campaignName?.message}>
            <Input
              {...register("campaignName", {
                required: "Enter a campaign name",
                maxLength: {
                  value: 100,
                  message: "Use 100 characters or fewer",
                },
              })}
              id="campaignName"
              placeholder="Client communications"
              maxLength={100}
              aria-invalid={!!errors.campaignName}
              className={cn("h-11", invalid(!!errors.campaignName))}
            />
          </Field>

          <Field
            id="useCase"
            label="Use case"
            hint="Appointment reminders, policy updates, client follow-ups and general client messages."
            error={errors.useCase?.message}
          >
            <Controller
              name="useCase"
              control={control}
              rules={{ required: "Choose a use case" }}
              render={({ field }) => (
                <Select value={field.value || RECOMMENDED_USE_CASE} onValueChange={field.onChange}>
                  <SelectTrigger id="useCase" aria-invalid={!!errors.useCase} className={cn("h-11", invalid(!!errors.useCase))}>
                    <SelectValue placeholder="Low Volume Mixed" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low Volume Mixed">Low Volume Mixed</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>

          <Field
            id="campaignDescription"
            label="Description"
            hint="Say how you text clients: appointment reminders, policy updates and follow-ups, and that they can reply STOP to opt out."
            error={errors.campaignDescription?.message}
          >
            <Textarea
              {...register("campaignDescription", {
                required: "Enter a description",
                minLength: {
                  value: 50,
                  message: "Use at least 50 characters",
                },
                maxLength: {
                  value: 500,
                  message: "Use 500 characters or fewer",
                },
                validate: (value: string) => {
                  const lower = value.toLowerCase();
                  const found = REJECTION_TRIGGER_WORDS.find(word => lower.includes(word));
                  if (found) return `Remove "${found}". Carriers reject descriptions that mention it.`;
                  return true;
                },
              })}
              id="campaignDescription"
              rows={5}
              maxLength={500}
              aria-invalid={!!errors.campaignDescription}
              className={cn("text-sm leading-relaxed", invalid(!!errors.campaignDescription))}
            />
            <p className="text-right text-xs tabular-nums text-neutral-500">{campaignDescription.length}/500</p>
          </Field>
        </Panel>
      </Section>

      <Section
        id="a2p-samples"
        title="Sample messages"
        description="Two to five examples of texts you'll send. Start from a template; each one already ends with the opt-out line."
      >
        <Panel className="divide-y divide-neutral-100">
          {fields.map((field, index) => (
            <div key={field.id} className="space-y-2.5 px-4 py-5 sm:px-5">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <label htmlFor={`sampleMessage-${index}`} className="text-sm font-medium text-neutral-900">
                  Message {index + 1}
                  {index >= 2 && <span className="font-normal text-neutral-500"> (optional)</span>}
                </label>
                <div className="flex items-center gap-2">
                  <Select
                    onValueChange={(templateLabel) => {
                      const template = SAMPLE_MESSAGE_TEMPLATES.find(t => t.label === templateLabel);
                      if (template) {
                        setValue(`sampleMessages.${index}.message`, personalizeTemplate(template.message, agentName, businessName));
                      }
                    }}
                  >
                    <SelectTrigger aria-label={`Template for message ${index + 1}`} className={cn("h-9 w-auto min-w-[11rem] text-xs", controlClass)}>
                      <SelectValue placeholder="Use a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {SAMPLE_MESSAGE_TEMPLATES.map((template) => (
                        <SelectItem key={template.label} value={template.label} className="text-sm">
                          {template.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {index >= 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      aria-label={`Remove message ${index + 1}`}
                      className={cn("size-9 text-neutral-500 hover:text-red-700", focusRing)}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>
              </div>

              <Textarea
                {...register(`sampleMessages.${index}.message` as const, {
                  required: index < 2 ? "Add at least 2 sample messages" : false,
                  maxLength: {
                    value: 160,
                    message: "Use 160 characters or fewer",
                  },
                  validate: {
                    noReplyYes: (value) => {
                      if (!value) return true;
                      // Block "Reply YES" patterns that cause Twilio rejection (error 30882)
                      const replyYesPattern = /reply\s+yes\s+(to\s+)?(confirm|receive|accept|agree|opt[- ]?in)/i;
                      if (replyYesPattern.test(value)) {
                        return "Don't ask people to reply YES to confirm; carriers reject it. End with \"Reply STOP to opt out\" instead.";
                      }
                      return true;
                    },
                  },
                })}
                id={`sampleMessage-${index}`}
                placeholder={'Hi [Name], your appointment is on [Date] at [Time]. Call me if you need to reschedule. Reply STOP to opt out.'}
                rows={3}
                maxLength={160}
                aria-invalid={!!errors.sampleMessages?.[index]?.message}
                className={cn("text-sm leading-relaxed", invalid(!!errors.sampleMessages?.[index]?.message))}
              />
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-red-700">{errors.sampleMessages?.[index]?.message?.message}</p>
                <p className="shrink-0 text-xs tabular-nums text-neutral-500">
                  {sampleMessages[index]?.message?.length || 0}/160
                </p>
              </div>
            </div>
          ))}
          <div className="px-4 py-4 sm:px-5">
            {fields.length < 5 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => append({ message: "" })}
                className={cn("h-11 w-full sm:h-10 sm:w-auto", focusRing)}
              >
                <Plus />
                Add a message
              </Button>
            ) : (
              <p className="text-sm text-neutral-500">That&apos;s the maximum of 5.</p>
            )}
          </div>
        </Panel>
        <p className="text-pretty text-xs leading-relaxed text-neutral-500">
          Every sample needs &ldquo;Reply STOP to opt out&rdquo;. Don&apos;t ask people to reply YES to confirm;
          carriers reject that.
        </p>
      </Section>

      <ReadOnlyField
        label="Daily limit"
        value={soleProp ? "About 1,000 texts a day, varies by carrier" : "Up to 6,000 message segments a day"}
        hint="Set by the carriers for this registration type."
      />
    </div>
  );
}
